import { useCallback, useEffect, useMemo, useState } from "react";
import { API, api, fmtBRL, fmtCompetencia, currentCompetencia, fetchLastCompetencia, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlayCircle, Zap, Eraser, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import BoletoFebraban from "@/components/BoletoFebraban";
import { printBoletoWithPageSize, fetchBeneficiario, prepararPix, mesclarPix } from "@/lib/boletoPrint";
import ComprovantePagamento from "@/components/ComprovantePagamento";

import { RateioPanel } from "@/components/financeiro/RateioPanel";
import { BoletosPanel } from "@/components/financeiro/BoletosPanel";
import { TrilhaAjustesMenu } from "@/components/financeiro/TrilhaAjustesMenu";

// Descrições canônicas do rateio dinâmico — usadas para upsert idempotente por (competencia + descricao).
const RATEIO_LABELS = {
  luz: { descricao: "Luz comum (rateio)" },
  outras: { descricao: "Outras despesas variáveis (rateio)" },
};

function Row({ label, v, bold, color, extra }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-bold text-base" : ""} ${color || ""}`}>
      <span>{label}</span>
      <span className="flex items-center gap-3">
        {extra && <span className="text-xs">{extra}</span>}
        <span>{fmtBRL(v)}</span>
      </span>
    </div>
  );
}

export default function AdminFinanceiro() {
  const [competencia, setCompetencia] = useState(currentCompetencia());
  const [rateio, setRateio] = useState(null);
  const [dre, setDre] = useState(null);
  const [boletos, setBoletos] = useState([]);
  const [iptu, setIptu] = useState({ valor_anual: 0, parcelas: 12, valor_parcela: 0 });
  const [novaDespesa, setNovaDespesa] = useState({ descricao: "", categoria: "VARIAVEL", valor: "", competencia });
  const [modalOpen, setModalOpen] = useState(false);
  const [iptuValor, setIptuValor] = useState("");
  const [rateioForm, setRateioForm] = useState({ luz: "", outras: "" });
  const [savingRateio, setSavingRateio] = useState(false);
  const [boletoImpressao, setBoletoImpressao] = useState(null);
  const [contratoImpressao, setContratoImpressao] = useState(null);
  const [beneficiario, setBeneficiario] = useState(null);

  // Alinha com a última competência que tem boletos ao montar
  useEffect(() => {
    fetchLastCompetencia().then((c) => c && setCompetencia(c));
  }, []);

  const load = useCallback(async () => {
    try {
      const [r, d, b, ip] = await Promise.all([
        api.get(`/financeiro/rateio/${competencia}`),
        api.get(`/financeiro/dre/${competencia}`),
        api.get(`/financeiro/boletos?competencia=${competencia}`),
        api.get("/financeiro/iptu"),
      ]);
      setRateio(r.data);
      setDre(d.data);
      setBoletos(b.data);
      setIptu(ip.data);
      setIptuValor(String(ip.data.valor_anual));

      // Pré-preenche o painel rápido com o que já foi lançado no mês
      const detalhes = [...(r.data.detalhes_variaveis || []), ...(r.data.detalhes_fixas || [])];
      const findValor = (desc) => {
        const found = detalhes.find((x) => x.descricao === desc);
        return found ? String(found.valor) : "";
      };
      setRateioForm({
        luz: findValor(RATEIO_LABELS.luz.descricao),
        outras: findValor(RATEIO_LABELS.outras.descricao),
      });
    } catch { /* ignore */ }
  }, [competencia]);

  useEffect(() => {
    load();
    // Polling em tempo real (a cada 30s) para refletir baixas do webhook Inter/Pix sem F5.
    const interval = setInterval(() => load(), 30_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const addDespesa = async () => {
    try {
      await api.post("/financeiro/despesas", { ...novaDespesa, valor: parseFloat(novaDespesa.valor), competencia });
      toast.success("Despesa adicionada");
      setNovaDespesa({ descricao: "", categoria: "VARIAVEL", valor: "", competencia, recorrente: false });
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao adicionar despesa");
    }
  };

  const delDespesa = async (id) => {
    await api.delete(`/financeiro/despesas/${id}`);
    toast.success("Despesa removida");
    load();
  };

  const salvarIptu = async () => {
    await api.put("/financeiro/iptu", { valor_anual: parseFloat(iptuValor), parcelas: 12 });
    toast.success("IPTU atualizado");
    load();
  };

  const fechar = async () => {
    const [anoStr, mesStr] = String(competencia).split("-");
    const ano = parseInt(anoStr, 10);
    const mes = parseInt(mesStr, 10);
    let anoV = ano, mesV = mes + 1;
    if (mesV > 12) { mesV = 1; anoV = ano + 1; }
    const sugestao = `${anoV}-${String(mesV).padStart(2, "0")}-10`;

    const dataVencimento = window.prompt(
      `Fechar competência ${competencia} — gerar boletos para os contratos ativos.\n\n` +
      `Data de VENCIMENTO dos boletos (YYYY-MM-DD).\n` +
      `• Deixe em branco para usar o dia de vencimento cadastrado em cada contrato (regra N+1).\n` +
      `• Ou informe uma data única (ex: ${sugestao}) que será aplicada a todos os boletos.`,
      sugestao
    );
    if (dataVencimento === null) return;
    const vencInformado = dataVencimento.trim();
    if (vencInformado && !/^\d{4}-\d{2}-\d{2}$/.test(vencInformado)) {
      toast.error("Data inválida — use o formato YYYY-MM-DD");
      return;
    }
    try {
      const url = vencInformado
        ? `/financeiro/fechamento/${competencia}?vencimento=${vencInformado}`
        : `/financeiro/fechamento/${competencia}`;
      const { data } = await api.post(url);
      const info = vencInformado ? ` (vencimento ${vencInformado})` : " (vencimento por contrato)";
      toast.success(`Fechamento executado — ${data?.boletos_gerados ?? 0} boleto(s) gerado(s)${info}`);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha no fechamento");
    }
  };

  const marcarPago = async (id) => {
    await api.post(`/pagamentos/admin/marcar-pago/${id}`);
    toast.success("Baixa manual aplicada");
    load();
  };

  const aplicarEncargosAgora = async () => {
    if (!window.confirm("Aplicar multa e mora em todos os boletos vencidos agora? O sistema já roda isso automaticamente às 03h UTC diariamente.")) return;
    try {
      const { data } = await api.post("/financeiro/aplicar-encargos");
      toast.success(
        `Encargos aplicados · ${data.boletos_processados} boleto(s) · ${data.multas_aplicadas_nesta_execucao} multa(s) nova(s) · Mora R$ ${data.mora_total_calculada}`
      );
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao aplicar encargos");
    }
  };

  const sincronizarVencimentos = async () => {
    if (!window.confirm(
      "Realinhar a data de vencimento de TODOS os boletos ABERTO/VENCIDO ao dia_vencimento cadastrado no contrato do lojista?\n\n" +
      "Boletos PAGOS não são afetados. Ideal quando um contrato teve seu dia_vencimento corrigido depois da emissão."
    )) return;
    try {
      const { data } = await api.post("/financeiro/sincronizar-vencimentos");
      if (data.ajustados === 0) {
        toast.info("Todos os boletos já estão alinhados aos contratos — nada a fazer");
      } else {
        toast.success(`${data.ajustados} boleto(s) realinhado(s) ao dia_vencimento do contrato`);
      }
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao sincronizar vencimentos");
    }
  };

  const autoRegenerarPixCancelados = async () => {
    if (!window.confirm(
      "Varrer TODOS os boletos ABERTO/VENCIDO e re-emitir Pix para aqueles que estão cancelados/expirados no gateway?\n\n" +
      "Esta rotina normalmente roda automaticamente às 03h BRT. Use aqui para forçar uma varredura imediata."
    )) return;
    try {
      const { data } = await api.post("/financeiro/regenerar-pix-cancelados");
      if (data.skip === "inter_mock_ativo") {
        toast.info("Gateway em modo MOCK — nada a regenerar");
        return;
      }
      const n = data?.regenerados ?? 0;
      const erros = data?.erros_consulta ?? 0;
      if (n === 0 && erros === 0) {
        toast.success(`Nenhum Pix cancelado encontrado (${data?.verificados || 0} verificados).`);
      } else if (n === 0 && erros > 0) {
        toast.warning(`Nenhuma regeneração — mas ${erros} consulta(s) falharam.`);
      } else {
        toast.success(`✅ ${n} Pix regenerado(s) automaticamente${erros > 0 ? ` · ${erros} falha(s) de consulta` : ""}`);
      }
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao regenerar Pix cancelados em massa");
    }
  };

  const editarVencimentoBoleto = async (boleto) => {
    const atual = (boleto.vencimento || "").slice(0, 10);
    const nova = window.prompt(
      `Novo vencimento para o boleto de ${boleto.razao_social} (${boleto.competencia})\n\n` +
      `Formato: YYYY-MM-DD (ex: 2026-02-15)\n` +
      `Atual: ${atual}`,
      atual
    );
    if (!nova || nova === atual) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(nova.trim())) {
      toast.error("Formato inválido. Use YYYY-MM-DD.");
      return;
    }
    try {
      await api.patch(`/financeiro/boletos/${boleto.id}/vencimento`, { vencimento: nova.trim() });
      toast.success(`Vencimento atualizado para ${nova.trim()}`);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao atualizar vencimento");
    }
  };

  const regenerarPix = async (boleto) => {
    if (!window.confirm(
      `Regenerar QR Code Pix do boleto de ${boleto.razao_social}?\n\n` +
      `Isso descarta o QR antigo e emite um novo com o valor atualizado (R$ ${boleto.valor_total?.toFixed(2)}). ` +
      `Use quando o valor mudou por encargos ou vencimento foi corrigido.`
    )) return;
    try {
      const { data } = await api.post(`/pagamentos/pix/regenerar/${boleto.id}`);
      const antes = Number(data.valor_anterior || 0);
      const depois = Number(data.valor_pix || 0);
      if (antes && Math.abs(antes - depois) > 0.01) {
        toast.success(`✅ Pix regenerado · R$ ${antes.toFixed(2)} → R$ ${depois.toFixed(2)}`);
      } else {
        toast.success(`✅ Pix regenerado · R$ ${depois.toFixed(2)}`);
      }
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao regenerar Pix");
    }
  };

  const isentarJuros = async (boleto) => {
    const multa = Number(boleto.multa || 0);
    const juros = Number(boleto.juros || 0);
    const total = multa + juros;
    if (total < 0.01) { toast.error("Este boleto não tem juros/multa aplicados."); return; }
    const ok = window.confirm(
      `💚 Isentar juros pontualmente\n\n` +
      `Lojista: ${boleto.razao_social}\n` +
      `Competência: ${boleto.competencia}\n\n` +
      `Multa a abonar: ${fmtBRL(multa)}\n` +
      `Juros a abonar: ${fmtBRL(juros)}\n` +
      `TOTAL a abonar: ${fmtBRL(total)}\n\n` +
      `Valor do boleto: ${fmtBRL(Number(boleto.valor_total || 0))} → ${fmtBRL(Number(boleto.valor_total || 0) - total)}\n\n` +
      `Esta isenção vale APENAS para esta competência.\n` +
      `O QR Pix atual será invalidado e um novo será gerado com o valor sem juros.\n\n` +
      `Confirmar isenção?`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/pagamentos/boletos/${boleto.id}/isentar-juros`);
      toast.success(
        `✅ Juros isentos: ${fmtBRL(data.total_abonado)} abonados · Boleto ${fmtBRL(data.valor_total_anterior)} → ${fmtBRL(data.valor_total_novo)}`
      );
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao isentar juros");
    }
  };

  const adicionarAjuste = async (boleto) => {
    const desc = window.prompt(
      `Lançar ajuste/resíduo no boleto de ${boleto.razao_social} (${boleto.competencia})\n\n` +
      `Descreva o ajuste (ex: "Diferença de Rateio - Mês Anterior", "Crédito estorno"):`,
      "Diferença de Rateio - Mês Anterior"
    );
    if (!desc || !desc.trim()) return;
    const valorStr = window.prompt(
      `Valor do ajuste em reais\n\n` +
      `POSITIVO aumenta o boleto (ex: 12.50)\n` +
      `NEGATIVO gera crédito (ex: -30.00)\n\n` +
      `Use ponto como separador decimal.`,
      "0.00"
    );
    if (!valorStr) return;
    const valor = parseFloat(valorStr.replace(",", "."));
    if (!Number.isFinite(valor) || Math.abs(valor) < 0.01) {
      toast.error("Valor inválido. Informe um número ≠ 0.");
      return;
    }
    try {
      const { data } = await api.post(`/pagamentos/boletos/${boleto.id}/ajustes`, {
        descricao: desc.trim(),
        valor: Math.round(valor * 100) / 100,
      });
      const antes = Number(data.valor_total_anterior || 0);
      const depois = Number(data.valor_total_atual || 0);
      toast.success(
        `✅ Ajuste lançado: "${desc.trim()}" · R$ ${valor.toFixed(2)} · Boleto: R$ ${antes.toFixed(2)} → R$ ${depois.toFixed(2)}` +
        (data.pix_invalidado ? " · Pix regenerado" : "")
      );
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao lançar ajuste");
    }
  };

  const imprimirBoleto = async (boleto) => {
    try {
      const [contratoResp, benef, pix] = await Promise.all([
        api.get(`/contratos/${boleto.contrato_id}`),
        beneficiario ? Promise.resolve(beneficiario) : fetchBeneficiario(),
        boleto.status !== "PAGO" ? prepararPix(boleto.id, "admin") : Promise.resolve(null),
      ]);
      if (benef && !beneficiario) setBeneficiario(benef);
      setBoletoImpressao(mesclarPix(boleto, pix));
      setContratoImpressao(contratoResp.data);
      setTimeout(() => {
        printBoletoWithPageSize();
        setTimeout(() => { setBoletoImpressao(null); setContratoImpressao(null); }, 500);
      }, 300);
    } catch (e) {
      toast.error(extractError(e) || "Falha ao carregar dados para impressão");
    }
  };

  // Salva os 2 lançamentos padronizados de rateio dinâmico (Luz/Outras).
  // Estratégia: apaga o lançamento anterior com a descrição canônica na competência atual e cria o novo (se >0).
  const salvarRateioDinamico = async () => {
    setSavingRateio(true);
    try {
      const detalhes = [...(rateio?.detalhes_variaveis || []), ...(rateio?.detalhes_fixas || [])];
      for (const key of ["luz", "outras"]) {
        const label = RATEIO_LABELS[key].descricao;
        const raw = rateioForm[key];
        const valor = parseFloat(raw);
        const existente = detalhes.find((d) => d.descricao === label);
        if (existente) await api.delete(`/financeiro/despesas/${existente.id}`);
        if (!Number.isNaN(valor) && valor > 0) {
          await api.post("/financeiro/despesas", { descricao: label, categoria: "VARIAVEL", valor, competencia });
        }
      }
      toast.success("Rateio de despesas dinâmicas atualizado");
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao salvar rateio");
    } finally {
      setSavingRateio(false);
    }
  };

  const totalRateioDinamico = useMemo(
    () => (parseFloat(rateioForm.luz) || 0) + (parseFloat(rateioForm.outras) || 0),
    [rateioForm]
  );

  const excluirBoleto = async (b) => {
    const alvo = `${b.razao_social} · ${b.competencia} · ${fmtBRL(b.valor_total)}`;
    const aviso = b.status === "PAGO"
      ? `⚠️ Este boleto está PAGO. Excluí-lo remove o registro do histórico (uso típico: limpeza de dados de teste).\n\n${alvo}\n\nCadastros de lojistas, contratos, unidades e configurações permanecem intactos.`
      : `Excluir este boleto?\n\n${alvo}\n\nEsta ação cancela a cobrança e remove o boleto. Contratos, lojistas, unidades e configurações permanecem intactos.`;
    if (!window.confirm(aviso)) return;
    try {
      const { data } = await api.delete(`/financeiro/boletos/${b.id}`);
      toast.success(data?.mensagem || "Boleto excluído");
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao excluir boleto");
    }
  };

  const exportarTrilhaAjustes = async (formato, escopo) => {
    try {
      const params = escopo === "competencia" ? `?competencia=${competencia}` : "";
      const url = `${API}/trilha-ajustes/${formato}${params}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => "");
        throw new Error(txt || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const fileURL = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileURL;
      const suffix = escopo === "competencia" ? `_${competencia}` : "_completo";
      link.download = `trilha_ajustes${suffix}.${formato}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(fileURL), 2000);
      toast.success(`Trilha de ajustes exportada em ${formato.toUpperCase()}`);
    } catch (e) {
      toast.error(`Falha ao exportar: ${e?.message || "erro desconhecido"}`);
    }
  };

  const sincronizarInter = async () => {
    if (!window.confirm(
      "Sincronizar com o Banco Inter?\n\n" +
      "Vai consultar todas as faturas ABERTO/VENCIDO com Pix/Boleto e dar baixa nas que já foram pagas mas não receberam webhook."
    )) return;
    try {
      const { data } = await api.post("/pagamentos/sincronizar");
      const n = data?.boletos_baixados ?? 0;
      const erros = data?.erros_consulta ?? 0;
      if (n === 0) {
        if (erros > 0) {
          toast.warning(`Nenhuma nova baixa — mas ${erros} consulta(s) falharam. Verifique credenciais no /api/webhook/log ou logs do backend.`);
        } else {
          toast.success(`Sincronização concluída — nenhuma nova baixa (${data?.consultados || 0} pagamentos consultados).`);
        }
      } else {
        toast.success(`✅ ${n} boleto(s) baixado(s): ${(data.detalhes || []).map((d) => d.razao_social).slice(0, 3).join(", ")}${n > 3 ? "..." : ""}`);
        if (erros > 0) toast.warning(`${erros} consulta(s) falhou — pode haver boletos ainda não sincronizados.`);
      }
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao sincronizar com o gateway");
    }
  };

  const dispararCobrancasMes = async () => {
    if (!window.confirm(
      `Disparar cobranças (WhatsApp + E-mail) para TODOS os boletos ABERTO/VENCIDO da competência ${competencia}?\n\n` +
      `Cada lojista receberá um aviso amigável de cobrança com link para o Portal do Lojista.\n\n` +
      `⚠️ Anti-spam ativo: intervalo aleatório de 10-15s entre cada mensagem. O disparo roda em background.`
    )) return;
    try {
      const { data } = await api.post("/whatsapp/cobrancas/disparar", { competencia, incluir_pagos: false });
      const total = data?.total_previsto ?? 0;
      const eta = data?.estimativa_minutos ?? 0;
      if (total === 0) {
        toast.info("Nenhum boleto ABERTO/VENCIDO nesta competência — nada a enviar.");
      } else {
        toast.success(`🚀 Disparo iniciado em background · ${total} mensagens · ETA ~${eta} min · Job ${data?.job_id?.slice(-6)}`);
      }
    } catch (e) {
      toast.error(extractError(e) || "Falha ao disparar cobranças");
    }
  };

  const dispararCobrancaBoleto = async (b) => {
    if (!window.confirm(`Enviar cobrança AGORA para ${b.razao_social}?\n\nWhatsApp + E-mail com Pix real e link do boleto.`)) return;
    try {
      const { data } = await api.post(`/whatsapp/cobrancas/boleto/${b.id}`);
      const wa = data.whatsapp;
      if (wa === "ENVIADO" || wa === "ENVIADO_MOCK") {
        toast.success(`✅ Mensagem enviada com sucesso para o WhatsApp do lojista!`);
      } else if (wa === "SEM_TELEFONE") {
        toast.warning("Lojista sem telefone cadastrado — apenas e-mail foi enviado.");
      } else if (wa === "FALHA") {
        toast.error(`❌ Falha no envio WhatsApp: ${data.whatsapp_erro || "verifique número/conexão Z-API"}`);
      } else {
        toast.success(`Cobrança processada — WhatsApp: ${wa || "-"} · E-mail: ${data.email || "-"}`);
      }
    } catch (e) {
      toast.error(extractError(e) || "Falha ao enviar cobrança");
    }
  };

  const zerarBoletosMes = async () => {
    const conf = window.prompt(
      `⚠️ ZERAR TODOS OS BOLETOS DA COMPETÊNCIA ${competencia}?\n\n` +
      `Isso remove TODAS as faturas deste mês (inclusive as PAGAS) e cancela cobranças pendentes. ` +
      `Cadastros de lojistas, contratos, unidades e configurações NÃO são afetados.\n\n` +
      `Para confirmar, digite exatamente: ZERAR`
    );
    if (conf !== "ZERAR") {
      if (conf !== null) toast.error("Confirmação incorreta — nada foi feito.");
      return;
    }
    try {
      const { data } = await api.post("/financeiro/boletos/zerar-mes", { competencia, confirmation: "ZERAR", incluir_pagos: true });
      toast.success(data?.mensagem || `${data?.boletos_removidos || 0} boleto(s) removido(s)`);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao zerar boletos do mês");
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-tag mb-2">Motor de rateio · Fechamento · DRE</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Financeiro</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="fin-competencia" type="month" value={competencia}
            onChange={(e) => setCompetencia(e.target.value)} className="w-44 rounded-sm" />
          <Button data-testid="fin-fechamento" onClick={fechar}
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90">
            <PlayCircle size={14} className="mr-2" /> Fechar mês
          </Button>
          <Button data-testid="fin-sincronizar-inter" onClick={sincronizarInter} variant="outline"
            className="rounded-sm border-success/50 text-success hover:bg-success/10"
            title="Consultar o Banco Inter e dar baixa em faturas já pagas">
            <RefreshCw size={14} className="mr-2" /> Sincronizar
          </Button>
          <Button data-testid="fin-disparar-cobrancas" onClick={dispararCobrancasMes}
            className="rounded-sm bg-info text-background hover:bg-info/90"
            title={`Enviar WhatsApp + E-mail para todos os boletos em aberto de ${competencia}`}>
            <Send size={14} className="mr-2" /> Disparar Cobranças
          </Button>
          <Button data-testid="fin-zerar-mes" onClick={zerarBoletosMes} variant="outline"
            className="rounded-sm border-destructive/50 text-destructive hover:bg-destructive/10"
            title={`Excluir todos os boletos não pagos de ${competencia}`}>
            <Eraser size={14} className="mr-2" /> Zerar boletos do mês
          </Button>
          <TrilhaAjustesMenu competencia={competencia} onExport={exportarTrilhaAjustes} />
        </div>
      </div>

      <Tabs defaultValue="rateio">
        <TabsList className="rounded-sm">
          <TabsTrigger data-testid="tab-rateio" value="rateio">Rateio & Despesas</TabsTrigger>
          <TabsTrigger data-testid="tab-dre" value="dre">DRE / DFC</TabsTrigger>
          <TabsTrigger data-testid="tab-boletos" value="boletos">Boletos</TabsTrigger>
          <TabsTrigger data-testid="tab-iptu" value="iptu">IPTU do Terreno</TabsTrigger>
        </TabsList>

        <TabsContent value="rateio" className="mt-6">
          <RateioPanel
            rateio={rateio} rateioForm={rateioForm} setRateioForm={setRateioForm}
            totalRateioDinamico={totalRateioDinamico}
            savingRateio={savingRateio} onSalvarRateio={salvarRateioDinamico}
            modalOpen={modalOpen} setModalOpen={setModalOpen}
            novaDespesa={novaDespesa} setNovaDespesa={setNovaDespesa}
            onAddDespesa={addDespesa} onDelDespesa={delDespesa} onChanged={load}
          />
        </TabsContent>

        <TabsContent value="dre" className="mt-6">
          {dre && (
            <Card className="p-8 rounded-sm border-border">
              <h2 className="font-heading font-bold text-xl mb-6">DRE · {fmtCompetencia(dre.competencia)}</h2>
              <div className="space-y-4 font-mono text-sm">
                <Row label="(+) Receita de aluguéis" v={dre.receita_aluguel} />
                <Row label="(+) Receita de rateio" v={dre.receita_rateio} />
                <Row label="(=) Receita bruta total" v={dre.receita_bruta_total} bold />
                <Row label="(−) Despesas variáveis" v={-dre.despesas_variaveis} />
                <Row label="(=) Margem de contribuição" v={dre.margem_contribuicao} bold color="text-info" extra={`${dre.margem_contribuicao_percent}%`} />
                <Row label="(−) Despesas fixas" v={-dre.despesas_fixas} />
                <div className="border-t-2 border-foreground pt-4">
                  <Row label="(=) LUCRO LÍQUIDO" v={dre.lucro_liquido} bold
                    color={dre.lucro_liquido >= 0 ? "text-success" : "text-destructive"}
                    extra={`${dre.lucro_liquido_percent}%`} />
                </div>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="boletos" className="mt-6">
          <BoletosPanel
            competencia={competencia} boletos={boletos}
            onSincronizarVencimentos={sincronizarVencimentos}
            onAutoRegenerarPix={autoRegenerarPixCancelados}
            onAplicarEncargos={aplicarEncargosAgora}
            onMarcarPago={marcarPago} onEditarVenc={editarVencimentoBoleto}
            onRegenerarPix={regenerarPix} onAjuste={adicionarAjuste}
            onIsentarJuros={isentarJuros} onImprimir={imprimirBoleto}
            onCobranca={dispararCobrancaBoleto} onExcluir={excluirBoleto}
          />
        </TabsContent>

        <TabsContent value="iptu" className="mt-6">
          <Card className="p-8 rounded-sm border-border max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <Zap className="text-warning" size={20} />
              <h2 className="font-heading font-bold text-xl">IPTU do Terreno · {iptu.ano_referencia}</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              O valor anual dividido em 12 parcelas mensais. Cada parcela é rateada entre os <b>{rateio?.total_estandes || 30}</b> estandes container quando o IPTU individual não estiver cadastrado na unidade.
              Lojas <b>não</b> pagam esse IPTU do terreno — têm cobrança própria pela prefeitura (cadastrada por unidade).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <div className="stat-block p-4 rounded-sm">
                <div className="label-tag">Anual</div>
                <div className="stat-num text-2xl mt-2">{fmtBRL(iptu.valor_anual)}</div>
              </div>
              <div className="stat-block p-4 rounded-sm">
                <div className="label-tag">Mensal</div>
                <div className="stat-num text-2xl mt-2">{fmtBRL(iptu.valor_parcela)}</div>
              </div>
              <div className="stat-block p-4 rounded-sm">
                <div className="label-tag">Por estande</div>
                <div className="stat-num text-2xl mt-2 text-warning">{fmtBRL(iptu.valor_parcela / (rateio?.total_estandes || 30))}</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input data-testid="iptu-input" type="number" step="0.01" value={iptuValor}
                onChange={(e) => setIptuValor(e.target.value)} placeholder="Valor anual" className="rounded-sm" />
              <Button data-testid="iptu-salvar" onClick={salvarIptu}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90">Atualizar</Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {boletoImpressao && contratoImpressao && (
        <div className="print-only">
          {boletoImpressao.status === "PAGO"
            ? <ComprovantePagamento fatura={boletoImpressao} contrato={contratoImpressao} beneficiario={beneficiario} />
            : <BoletoFebraban boleto={boletoImpressao} contrato={contratoImpressao} beneficiario={beneficiario} />}
        </div>
      )}
    </div>
  );
}
